import { isToday, format } from 'date-fns'
import { useAppMutation } from '@/helpers/api/mutation'

import { createInstance } from '@/helpers/api/base'
import { useNotification } from '@/ui/Notification'
import { Log } from '@/types/logs'
import { useModal, DeleteModalProps, ModalTypes } from '@/components/Modal'
import { useGetCodes } from './useGetCodes'
import { useGetControllerLogParams } from '../apiHelpers/useGetControllerLogParams'

export const useDeleteControllerLog = (log: Log) => {
    const { showNotification } = useNotification()
    const { openModal } = useModal()
    const { data: codes = [] } = useGetCodes()
    const logParams = useGetControllerLogParams()

    const deleteControllerLog = useAppMutation<void, Error, string, Log[]>({
        mutationFn: (logId: string) =>
            createInstance({
                url: `/controller-logs/${logId}`,
                method: 'DELETE',
            }),
        alertOnError: true,
        queryKey: ['controllerLogs', ...logParams],
        optimisticChangesCallback: (requestBody: string, previousLogs?: Log[]) =>
            previousLogs ? previousLogs.filter((item) => item.id !== requestBody) : [],
        onSuccess: () => {
            showNotification({ message: 'Record deleted' })
        },
    })

    const handleDeleteControllerLog = () => {
        const deleteFrom = log.timestamp ? (isToday(log.timestamp) ? 'today' : format(log.timestamp, 'd/MM/y')) : null
        const codeData = codes.find((item) => item.code === log.code)
        const displayFallback = !log.code || !log.reference

        openModal<DeleteModalProps>(ModalTypes.deleteModal, {
            title: 'Delete record?',
            message: (
                <>
                    This will delete{' '}
                    <strong>
                        {displayFallback ? (
                            'this item'
                        ) : (
                            <>
                                {codeData?.description || log.code}
                                {log.reference && <> {log.reference}</>}
                            </>
                        )}
                    </strong>
                    {deleteFrom && (
                        <>
                            {' '}
                            from <strong>{deleteFrom}</strong>
                        </>
                    )}
                    . This cannot be undone.
                </>
            ),
            onDelete: () => {
                deleteControllerLog.mutate(log.id)
            },
        })
    }

    return {
        handleDeleteControllerLog,
    }
}
